import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { resolveUrl } from '../api';
import { createWalkieRouter, huntQApi, voiceToPlay, type HeardLine, type VoiceMessage } from '../q/huntQ';

const POLL_MS = 4000;

/**
 * Q i jakten (QJ1–QJ3, MT1) för KartaTab: rapporterar det telefonen hör, hämtar det de andra hört,
 * skickar och spelar upp walkie-talkie-meddelanden och säger när radion startats av någon.
 * Bara för inloggade (gäster rapporterar inget och ansluter radion direkt som förut).
 */
export function useHuntQ({ meetingId, enabled, speakerOn, onRemoteHeard, onPlayback }: {
  meetingId: string | null;
  enabled: boolean;
  /** Avstängd högtalare: röstmeddelanden spelas inte upp automatiskt (de väntar i Q-fliken). */
  speakerOn: boolean;
  /** Rader som andra deltagares telefoner hört. */
  onRemoteHeard: (lines: HeardLine[]) => void;
  /** Uppspelning börjar/slutar (jakten pausar då sin egen lyssning, så att meddelandet inte transkriberas igen). */
  onPlayback?: (playing: boolean) => void;
}) {
  const [voice, setVoice] = useState<VoiceMessage[]>([]);
  const [radioStartedAt, setRadioStartedAt] = useState<number | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const active = enabled && !!meetingId;
  const cb = useRef({ onRemoteHeard, onPlayback, speakerOn });
  cb.current = { onRemoteHeard, onPlayback, speakerOn };
  const played = useRef(new Set<string>());
  const playing = useRef<HTMLAudioElement | null>(null);
  const router = useMemo(() => createWalkieRouter(), [meetingId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setVoice([]); setRadioStartedAt(null); setSummary(null); setNotice(null);
    played.current = new Set();
  }, [meetingId]);

  const markHeard = useCallback((id: string) => {
    setVoice(prev => prev.map(v => (v.id === id ? { ...v, heard: true } : v)));
    if (meetingId) huntQApi.voiceHeard(meetingId, id).catch(() => {});
  }, [meetingId]);

  /** Spelar upp ett röstmeddelande (ljud, annars texten med talsyntes). */
  const playVoice = useCallback(async (v: VoiceMessage) => {
    played.current.add(v.id);
    cb.current.onPlayback?.(true);
    const done = () => { cb.current.onPlayback?.(false); markHeard(v.id); };
    try {
      if (v.audioUrl) {
        playing.current?.pause();
        const a = new Audio(resolveUrl(v.audioUrl));
        playing.current = a;
        await new Promise<void>((resolve, reject) => {
          a.onended = () => resolve();
          a.onerror = () => reject(new Error('ljudet kunde inte spelas'));
          a.play().catch(reject);
        });
        done();
      } else if (v.text && window.speechSynthesis) {
        const u = new SpeechSynthesisUtterance(`${v.from}: ${v.text}`);
        u.lang = 'sv-SE';
        u.onend = done;
        u.onerror = done;
        window.speechSynthesis.speak(u);
      } else {
        done();
      }
    } catch {
      // Autouppspelning nekad (eller fel): meddelandet ligger kvar som nytt i Q-fliken.
      played.current.delete(v.id);
      cb.current.onPlayback?.(false);
    }
  }, [markHeard]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let after: number | undefined;
    const poll = async () => {
      try {
        const f = await huntQApi.feed(meetingId!, after);
        if (cancelled) return;
        const newest = Math.max(after ?? 0, ...f.heard.map(h => h.ts), ...f.voice.map(v => v.ts));
        if (newest > 0) after = newest;
        const others = f.heard.filter(h => !h.isMe);
        if (others.length) cb.current.onRemoteHeard(others);
        if (f.voice.length) {
          setVoice(prev => {
            const byId = new Map(prev.map(v => [v.id, v]));
            for (const v of f.voice) byId.set(v.id, { ...v, heard: v.heard || !!byId.get(v.id)?.heard });
            return [...byId.values()].sort((a, b) => a.ts - b.ts);
          });
          if (cb.current.speakerOn) for (const v of voiceToPlay(f.voice, played.current)) void playVoice(v);
        }
        setRadioStartedAt(f.radioStartedAt);
        if (f.summary) setSummary(f.summary);
      } catch { /* nästa intervall */ }
    };
    void poll();
    const tid = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(tid); };
  }, [active, meetingId, playVoice]);

  /** En rad som den här telefonen hört (tal eller skott) → servern, så att alla ser den i Q-fliken. */
  const reportHeard = useCallback((entry: { text: string; ts: number; lat?: number; lng?: number }) => {
    if (!active) return;
    const clientId = `h:${entry.ts}:${Math.random().toString(36).slice(2, 8)}`;
    huntQApi.heard(meetingId!, { clientId, text: entry.text, ts: entry.ts, ...(entry.lat != null && entry.lng != null ? { lat: entry.lat, lng: entry.lng } : {}) }).catch(() => {});
  }, [active, meetingId]);

  /**
   * QJ3: "Lasse till Ola …" i en nyss transkriberad bit → biten skickas som röstmeddelande till Ola.
   * Returnerar true om biten gick som walkie-meddelande. audio kan hämtas först när biten ska skickas
   * (I2: telefonens lokala lyssning kodar ljudet bara för walkie-anrop).
   */
  const routeWalkie = useCallback((text: string, audio: Blob | null | (() => Promise<Blob | null>)): boolean => {
    if (!active) return false;
    const target = router(text);
    if (!target) return false;
    (typeof audio === 'function' ? audio().catch(() => null) : Promise.resolve(audio))
      .then(blob => huntQApi.voice(meetingId!, target.to, { audio: blob, text: target.text || text }))
      .then(r => {
        setNotice(`Röstmeddelande skickat till ${r.to}`);
        setVoice(prev => [...prev, { id: r.id, from: 'Du', to: r.to, audioUrl: null, text: target.text || text, ts: Date.now(), heard: true, toMe: false }]);
      })
      .catch(e => setNotice(e instanceof Error ? e.message : 'Röstmeddelandet kunde inte skickas'));
    return true;
  }, [active, meetingId, router]);

  // Högtalaren slås på igen: spela det som väntat.
  useEffect(() => {
    if (!speakerOn) { playing.current?.pause(); return; }
    for (const v of voiceToPlay(voice, played.current)) void playVoice(v);
  }, [speakerOn]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(t);
  }, [notice]);

  const unheard = voice.filter(v => v.toMe && !v.heard).length;
  return { voice, radioStartedAt, summary, notice, unheard, reportHeard, routeWalkie, playVoice, setSummary };
}
